import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import { useAuth } from '@/stores/auth'
import { useSemesters } from '@/stores/semesters'

export interface NavigationItem {
    /** The `data-test` of the item. */
    test: string
    icon: string
    title: string
    to: RouteLocationRaw
    /** Active only on its own path (the course list is the home page). */
    exact?: boolean
}

export interface NavigationGroup {
    key: string
    /** Shown above the group's items; the course list has none. */
    title?: string
    items: NavigationItem[]
}

/**
 * The side panel's groups, each item shown only to the roles its page allows: the course list for everyone, then
 * "Moje", "Přehledy", "Odměny" and "Správa". Semester pages open on the course list's selected semester.
 */
export function useNavigation() {
    const { t } = useI18n()
    const auth = useAuth()
    const semesters = useSemesters()

    const groups = computed<NavigationGroup[]>(() => {
        const semester = semesters.selected ?? semesters.current ?? undefined
        const bySemester = semester === undefined ? {} : { semester }
        const isTeacher = auth.hasRole('teacher')
        const isAdmin = auth.hasRole('admin')
        const mine: NavigationItem[] = []
        if (isTeacher || auth.hasRole('external')) {
            mine.push({
                test: 'nav-my-statistics',
                icon: 'mdi-account-clock-outline',
                title: t('nav.myStatistics'),
                to: { name: 'statistics', query: bySemester },
            })
        }
        if (isTeacher && semester !== undefined) {
            mine.push({
                test: 'nav-projects',
                icon: 'mdi-clipboard-edit-outline',
                title: t('nav.projects'),
                to: { name: 'projects', params: { semester } },
            })
        }
        const overview: NavigationItem[] = isAdmin
            ? [
                  {
                      test: 'nav-teachers',
                      icon: 'mdi-account-group-outline',
                      title: t('statistics.teachers'),
                      to: { name: 'statistics-teachers' },
                  },
                  {
                      test: 'nav-course-statistics',
                      icon: 'mdi-chart-box-outline',
                      title: t('statistics.courses'),
                      to: { name: 'statistics-courses' },
                  },
              ]
            : []
        const rewards: NavigationItem[] = auth.hasRole('rewards')
            ? [
                  {
                      test: 'nav-rewards',
                      icon: 'mdi-cash-multiple',
                      title: t('nav.rewardsEnglish'),
                      to: { name: 'rewards', query: bySemester },
                  },
              ]
            : []
        const admin: NavigationItem[] = isAdmin
            ? [
                  {
                      test: 'nav-externals',
                      icon: 'mdi-account-key-outline',
                      title: t('externals.menu'),
                      to: { name: 'externals', query: bySemester },
                  },
              ]
            : []

        return [
            {
                key: 'courses',
                items: [
                    {
                        test: 'nav-courses',
                        icon: 'mdi-book-open-variant-outline',
                        title: t('nav.courses'),
                        to: { name: 'courses' },
                        exact: true,
                    },
                ],
            },
            { key: 'mine', title: t('nav.groups.mine'), items: mine },
            { key: 'overview', title: t('nav.groups.overview'), items: overview },
            { key: 'rewards', title: t('nav.groups.rewards'), items: rewards },
            { key: 'admin', title: t('nav.groups.admin'), items: admin },
        ].filter((group) => group.items.length > 0)
    })

    /** Someone who can only see the course list (a student) gets no panel. */
    const hasPanel = computed(() => groups.value.length > 1)

    return { groups, hasPanel }
}
